import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Field from "../../components/ui/Field";
import { authApi } from "../../services/auth";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // =====================
  // SUBMIT
  // =====================
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const em = email.trim();

    if (!em) {
      setError(t("forgot.errors.emailRequired"));
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(em)) {
      setError(t("forgot.errors.emailInvalid"));
      return;
    }

    try {
      setLoading(true);

      // 👉 gọi API thật
      await authApi.forgotPassword(em.toLowerCase());

      // ⚠️ luôn set sent = true (không leak user tồn tại hay không)
      setSent(true);
    } catch (err: any) {
      // backend có thể trả message, nhưng vẫn nên generic
      const apiMessage =
        err?.response?.data?.message || t("forgot.errors.unableToSend");

      setError(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-ivory flex items-center justify-center px-6">
      <div className="w-full max-w-[360px]">
        {/* BRAND */}
        <div className="mb-14">
          <p className="text-[9px] uppercase tracking-[0.6em] text-black/35">
            {t("forgot.brandKicker")}
          </p>
          <h1 className="mt-4 text-[22px] uppercase tracking-[0.35em]">
            {t("forgot.title")}
          </h1>
          <div className="mt-6 h-px w-12 bg-black/20" />
        </div>

        {/* FORM / CONFIRM */}
        {!sent ? (
          <form onSubmit={onSubmit} className="space-y-12">
            <p className="text-[11px] leading-relaxed text-black/50">
              {t("forgot.desc")}
            </p>

            <Field label={t("forgot.emailLabel")} error={error || undefined}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="lux-input"
              />
            </Field>

            <button
              type="submit"
              disabled={loading}
              className={`
                w-full h-10 rounded-full
                text-[9.5px] uppercase tracking-[0.55em]
                transition-all duration-300
                ${
                  loading
                    ? "bg-black/20 text-black/40 cursor-not-allowed"
                    : "bg-brand-brown text-brand-ivory hover:opacity-95"
                }
              `}
            >
              {loading ? t("forgot.sending") : t("forgot.sendResetLink")}
            </button>

            <div className="pt-4 text-center">
              <Link
                to="/auth"
                className="text-[9px] uppercase tracking-[0.4em] text-black/40 hover:text-black transition"
              >
                {t("forgot.backToLogin")}
              </Link>
            </div>
          </form>
        ) : (
          // =====================
          // CONFIRMATION
          // =====================
          <div className="space-y-10">
            <p className="text-[11px] leading-relaxed text-black/55">
              {t("forgot.sentLine1")}
              <span className="block mt-2 font-medium text-black">{email}</span>
              {t("forgot.sentLine2")}
            </p>

            <p className="text-[10px] uppercase tracking-[0.35em] text-black/40">
              {t("forgot.checkInbox")}
            </p>

            <Link
              to="/auth"
              className="
                inline-block
                text-[9px] uppercase tracking-[0.4em]
                text-black/40 hover:text-black
                transition
              "
            >
              {t("forgot.returnToLogin")}
            </Link>
          </div>
        )}

        {/* FOOT */}
        <p className="mt-16 text-[9px] uppercase tracking-[0.35em] text-black/30">
          {t("forgot.footerNote")}
        </p>
      </div>
    </div>
  );
}
